/**
 * Design System Tokens
 * Core visual styles and variables
 */

const darkTheme = {
  colors: {
    // Brand colors - Vibrant & Modern
    primary: '#00B380',        // Deep Teal (easy on eyes)
    primaryDark: '#008F66',
    primaryLight: '#00D499',

    // Secondary/Accent
    secondary: '#7000FF',      // Electric Purple
    secondaryLight: '#9D4DFF',
    secondaryDark: '#5200CC',

    // Background colors
    background: '#050505',     // Almost Black
    surface: '#121212',        // Dark Gray
    surfaceLight: '#1E1E1E',
    surfaceLighter: '#2D2D2D',

    // Elevation levels
    surfaceLvl0: '#050505',
    surfaceLvl1: '#121212',
    surfaceLvl2: '#1E1E1E',
    surfaceLvl3: '#2D2D2D',

    // Text colors
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    textTertiary: 'rgba(255, 255, 255, 0.5)',
    textDisabled: 'rgba(255, 255, 255, 0.3)',

    // Border colors
    border: 'rgba(255, 255, 255, 0.1)',
    borderLight: 'rgba(255, 255, 255, 0.15)',
    borderDark: 'rgba(255, 255, 255, 0.05)',

    // Glassmorphism System
    glass: {
      background: 'rgba(18, 18, 18, 0.65)',
      border: 'rgba(255, 255, 255, 0.08)',
      text: '#FFFFFF',
      blur: '12px'
    },
    glassHigh: {
      background: 'rgba(30, 30, 30, 0.8)',
      border: 'rgba(255, 255, 255, 0.12)',
      text: '#FFFFFF',
      blur: '16px'
    },
    glassLow: {
      background: 'rgba(10, 10, 10, 0.4)',
      border: 'rgba(255, 255, 255, 0.05)',
      text: 'rgba(255, 255, 255, 0.8)',
      blur: '8px'
    },

    // Form colors
    inputBorder: 'rgba(255, 255, 255, 0.15)',
    inputBackground: 'rgba(0, 0, 0, 0.3)',
    inputText: '#FFFFFF',
    inputPlaceholder: 'rgba(255, 255, 255, 0.4)',

    // Status colors - Neon/Vibrant
    success: '#00B380',
    successDark: '#008F66',
    successLight: 'rgba(0, 179, 128, 0.15)',

    error: '#FF3366',
    errorDark: '#CC2952',
    errorLight: 'rgba(255, 51, 102, 0.15)',

    warning: '#FFB800',
    warningDark: '#CC9300',
    warningLight: 'rgba(255, 184, 0, 0.15)',

    info: '#00D4FF',
    infoDark: '#00AACC',
    infoLight: 'rgba(0, 212, 255, 0.15)',

    // Role colors
    roleAdmin: '#FF3366',
    roleAdminLight: 'rgba(255, 51, 102, 0.2)',
    roleClubAdmin: '#B366FF',
    roleClubAdminLight: 'rgba(179, 102, 255, 0.2)',
    roleCoach: '#FFB800',
    roleCoachLight: 'rgba(255, 184, 0, 0.2)',
    rolePlayer: '#00D4FF',
    rolePlayerLight: 'rgba(0, 212, 255, 0.2)',

    // Specialty colors
    accent: '#7000FF',
    accentLight: 'rgba(112, 0, 255, 0.2)',

    // Brand icon color (WorldsPitch logo/icon only)
    brandIcon: '#00FF94',        // Original Neon Mint for logo/brand
    brandIconGlow: 'rgba(0, 255, 148, 0.3)',

    // Action colors (Semantic)
    actionView: '#00D4FF',
    actionViewHover: '#00AACC',
    actionViewLight: 'rgba(0, 212, 255, 0.1)',

    actionEdit: '#00E5FF',
    actionEditHover: '#00B8CC',
    actionEditLight: 'rgba(0, 229, 255, 0.1)',

    actionDelete: '#FF3366',
    actionDeleteHover: '#CC2952',
    actionDeleteLight: 'rgba(255, 51, 102, 0.1)',

    actionSave: '#00B380',
    actionSaveHover: '#008F66',
    actionSaveLight: 'rgba(0, 179, 128, 0.1)',

    actionCancel: '#888888',
    actionCancelHover: '#666666',
    actionCancelLight: 'rgba(136, 136, 136, 0.1)',

    actionWarning: '#FFB800',
    actionWarningHover: '#CC9300',
    actionWarningLight: 'rgba(255, 184, 0, 0.1)',

    // Interactive states
    hover: 'rgba(255, 255, 255, 0.05)',
    focus: 'rgba(0, 179, 128, 0.25)',
    active: 'rgba(255, 255, 255, 0.1)',
    disabled: 'rgba(255, 255, 255, 0.1)',
    disabledText: 'rgba(255, 255, 255, 0.3)',

    // Overlays
    overlay: 'rgba(0, 0, 0, 0.8)',
    overlayLight: 'rgba(255, 255, 255, 0.03)',
    overlayMedium: 'rgba(255, 255, 255, 0.08)',
    overlayHeavy: 'rgba(255, 255, 255, 0.12)',

    // Component backgrounds
    primaryBackground: 'rgba(0, 179, 128, 0.1)',
    footerBackground: '#121212', // Matches surface
    pageGradient: 'linear-gradient(135deg, #050505 0%, #121212 100%)',
    modalBackdrop: 'rgba(0, 0, 0, 0.85)'
  },

  typography: {
    fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    weights: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '2rem',
      '4xl': '2.5rem',
      '5xl': '3rem'
    }
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem'
  },

  borderRadius: {
    sm: '6px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    full: '9999px'
  },

  shadows: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.2)',
    md: '0 8px 24px rgba(0, 0, 0, 0.3)',
    lg: '0 16px 48px rgba(0, 0, 0, 0.4), 0 0 100px rgba(0, 179, 128, 0.05)',
    xl: '0 24px 60px rgba(0, 0, 0, 0.5), 0 0 150px rgba(0, 179, 128, 0.08)'
  },

  transitions: {
    fast: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)'
  }
};

const lightTheme = {
  // Keeping light theme simple but updated for compliance, focusing on Dark Mode for "Sleek" look
  colors: {
    primary: '#00CC76',
    primaryDark: '#00A35E',
    primaryLight: '#E0FFEF',

    secondary: '#6200EE',
    secondaryLight: '#B794F6',
    secondaryDark: '#3700B3',

    textPrimary: '#111827',
    textSecondary: '#4B5563',
    textTertiary: '#9CA3AF',
    textDisabled: '#D1D5DB',

    // Backgrounds - Neutral Gray
    background: '#F9FAFB',
    surface: '#FFFFFF',
    surfaceLight: '#F3F4F6',
    surfaceLighter: '#E5E7EB',

    border: '#E5E7EB',
    borderLight: '#F3F4F6',
    borderDark: '#D1D5DB',

    glass: {
      background: 'rgba(255, 255, 255, 0.7)',
      border: 'rgba(255, 255, 255, 0.5)',
      text: '#111827',
      blur: '12px'
    },
    glassHigh: {
      background: 'rgba(255, 255, 255, 0.9)',
      border: 'rgba(255, 255, 255, 0.8)',
      text: '#111827',
      blur: '16px'
    },
    glassLow: {
      background: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.3)',
      text: '#4B5563',
      blur: '8px'
    },

    inputBorder: '#D1D5DB',
    inputBackground: '#FFFFFF',
    inputText: '#111827',
    inputPlaceholder: '#9CA3AF',

    success: '#10B981',
    successDark: '#059669',
    successLight: '#D1FAE5',

    error: '#EF4444',
    errorDark: '#B91C1C',
    errorLight: '#FEE2E2',

    warning: '#F59E0B',
    warningDark: '#D97706',
    warningLight: '#FEF3C7',

    info: '#3B82F6',
    infoDark: '#2563EB',
    infoLight: '#DBEAFE',

    roleAdmin: '#EF4444',
    roleAdminLight: '#FEE2E2',
    roleClubAdmin: '#8B5CF6',
    roleClubAdminLight: '#EDE9FE',
    roleCoach: '#F59E0B',
    roleCoachLight: '#FEF3C7',
    rolePlayer: '#3B82F6',
    rolePlayerLight: '#DBEAFE',

    accent: '#6200EE',
    accentLight: '#EDE9FE',

    // Actions
    actionView: '#1976D2', // Darker blue for better contrast
    actionViewHover: '#1565C0',
    actionViewLight: '#DBEAFE',

    actionEdit: '#0891B2', // Darker cyan for better contrast
    actionEditHover: '#0E7490',
    actionEditLight: '#CFFAFE',

    actionDelete: '#EF4444',
    actionDeleteHover: '#DC2626',
    actionDeleteLight: '#FEE2E2',

    actionSave: '#388E3C', // Darker green for better contrast
    actionSaveHover: '#2E7D32',
    actionSaveLight: '#D1FAE5',

    actionCancel: '#6B7280',
    actionCancelHover: '#4B5563',
    actionCancelLight: '#F3F4F6',

    actionWarning: '#8B5CF6',
    actionWarningHover: '#7C3AED',
    actionWarningLight: '#EDE9FE',

    hover: 'rgba(0, 0, 0, 0.05)',
    focus: 'rgba(0, 204, 118, 0.25)',
    active: 'rgba(0, 0, 0, 0.1)',
    disabled: 'rgba(0, 0, 0, 0.1)',
    disabledText: 'rgba(0, 0, 0, 0.3)',

    overlay: 'rgba(0, 0, 0, 0.5)',
    overlayLight: 'rgba(0, 0, 0, 0.05)',
    overlayMedium: 'rgba(0, 0, 0, 0.1)',
    overlayHeavy: 'rgba(0, 0, 0, 0.2)',

    primaryBackground: 'rgba(0, 204, 118, 0.1)',
    footerBackground: '#FFFFFF', // Matches surface
    pageGradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
    modalBackdrop: 'rgba(0, 0, 0, 0.5)'
  },

  // Inherit structure from dark theme
  typography: darkTheme.typography,
  spacing: darkTheme.spacing,
  borderRadius: darkTheme.borderRadius,
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
  },
  transitions: darkTheme.transitions
};

export const themes = {
  dark: darkTheme,
  light: lightTheme
};